import { useContext, useEffect } from "react";
import { FormContainer as Container, FormSection as Section, InputField as Input } from "../../components";
import FormContext from "../../context/FormContext.js";
import { useInjectFormProps } from "../../hooks/useInjectInputs.js";
import StaticDataContext from "../../context/StaticDataContext.js";
import { createList } from "../../utils/utils.js";
import { validateStep } from "../../utils/pageValidator.js";
import { useNavigate } from "react-router-dom";
import { FORM_FIELDS } from "../../utils/constants.js";

const PreKNotEligible = () => (
    <div style={{ marginTop: "1rem", borderRadius: "var(--element-radius)", padding: "2px", border: "1px solid var(--error-border)", backgroundColor: "var(--error-background-color)", fontWeight: "500" }}>
        <ul>
            <li>In order to register a PreK student, approval from the school is required.</li>
            <li>If you have not yet completed the application process, please download, complete, and submit the applicable forms to the school:</li>
            <ul>
                <li><a href="https://www.gssd.ca/download/394356" target="_blank" rel="noopener noreferrer">Regular PreK form</a></li>
                <li><a href="https://forms.office.com/Pages/ResponsePage.aspx?id=ZNoGpeFT0EeomgBWsiNDGhQfsbUZcddPulGOZ93AosJUMk5YRjNUQzg1SlJZTlZQVEhMUjFDQ0ZaMC4u" target="_blank" rel="noopener noreferrer">Michif Early Learning Program form</a> (only available at Columbia School)</li>
            </ul>
            <li><b>Important: </b>Do not proceed with the registration form until you have received approval from the school.</li>
        </ul>
    </div>
);

const NonCadNotEligible = () => (
    <div style={{ textAlign: "center", marginTop: "1rem", borderRadius: "var(--element-radius)", padding: "5px", border: "1px solid var(--error-border)", backgroundColor: "var(--error-background-color)", fontWeight: "500" }}>
        <p>Please contact the Settlement Worker in Schools (SWIS) from the East Central Newcomer Welcome Centre to review your immigration documents and complete the registration process.</p>
        <br />
        <p>Unit H 132 Broadway Street W., Yorkton SK, Canada</p>
        <p>Phone: 306-783-2777</p>
        <p>E-mail: ecnwcinfo@ecnwc.ca</p>
        <a href="https://eastcentralnewcomercentre.ca/" target="_blank" rel="noreferrer">Website: East Central Newcomer Welcome Centre Inc.</a>
    </div>
);

const eligibilityRules = [
    {
        condition: (formData) => formData["isRegPrek"] === "1" && formData["isPreKApproved"] === "0",
        component: PreKNotEligible,
    },
    {
        condition: (formData) => formData["isCanCit"] === "0" && formData["workingWithSWIS"] === "0",
        component: NonCadNotEligible,
    },
    {
        condition: (formData) => formData["isCanCit"] === "1" || (formData["workingWithSWIS"] === "1" && formData["schoolReceiving"]),
        component: ({ onContinue }) => (
            <button type="button" onClick={onContinue} className="default-btn justify-center fade-in">
                Continue
            </button>
        ),
    },
];

const getEligibilityComponent = (formData) => {
    for (const rule of eligibilityRules) {
        if (rule.condition(formData)) {
            return rule.component;
        }
    }
    return null;
};

const testVar = process.env.REACT_APP_TESTVAR;

function InitialForm() {
    const navigate = useNavigate();

    useEffect(() => {
        if (!sessionStorage.getItem("started")) {
            navigate("/");
            return;
        }
    }, [navigate]);

    const { formData, updateFormData } = useContext(FormContext);
    const { staticData } = useContext(StaticDataContext);
    const { injectFormProps } = useInjectFormProps();

    const EligibilityComponent = getEligibilityComponent(formData);

    const beforeSubmit = () => {
        if (!validateStep()) {
            return;
        }
        if (formData[FORM_FIELDS.IS_CAN_CIT] === "1") {
            updateFormData({ schoolReceiving: "" });
            updateFormData({ workingWithSWIS: "" });
            navigate("/lafoip");
            return;
        } else if (formData[FORM_FIELDS.WORKING_WITH_SWIS] === "1") {
            updateFormData({ isCanCit: "0" });
            navigate("/swis-access");
            return;
        }
    };

    return (
        <Container>
            {injectFormProps(
                <>
                    <p style={{ fontSize: "1.8rem", fontWeight: "500" }}>Before you start:</p>
                    <hr />
                    <p>Let's find out if you're eligible to register.</p>
                    <Section>
                        <Input
                            type="noyesradio"
                            inputLabel={"Are you registering a PreK student?"}
                            identifier={FORM_FIELDS.IS_REG_PREK}
                        />
                        {formData[FORM_FIELDS.IS_REG_PREK] === "1" && (
                            <Input
                                type="noyesradio"
                                inputLabel={"Has the school approved the child’s Pre-K application?"}
                                identifier={FORM_FIELDS.IS_PREK_APPROVED}
                            />
                        )}
                        {(formData[FORM_FIELDS.IS_REG_PREK] === "0" || formData[FORM_FIELDS.IS_PREK_APPROVED] === "1") && (
                            <Input
                                type="noyesradio"
                                inputLabel={"Is the student a Canadian Citizen?"}
                                identifier={FORM_FIELDS.IS_CAN_CIT}
                            />
                        )}
                        {(formData[FORM_FIELDS.IS_CAN_CIT] === "0" &&
                            (formData[FORM_FIELDS.IS_REG_PREK] === "0" || formData[FORM_FIELDS.IS_PREK_APPROVED] === "1")) && (
                                <>
                                    <hr />
                                    <b>
                                        <i>
                                            For all non-Canadian Citizens, we require you to work alongside a Settlement Worker In Schools (SWIS) to register.
                                        </i>
                                    </b>
                                    <Input
                                        type="noyesradio"
                                        inputLabel={"Are you completing this form with a SWIS worker?"}
                                        identifier={FORM_FIELDS.WORKING_WITH_SWIS}
                                    />
                                </>
                            )}
                        {(formData[FORM_FIELDS.IS_CAN_CIT] === "0" &&
                            (formData[FORM_FIELDS.IS_REG_PREK] === "0" || formData[FORM_FIELDS.IS_PREK_APPROVED] === "1") &&
                            formData[FORM_FIELDS.WORKING_WITH_SWIS] === "1") && (
                                <>
                                    <Input
                                        type="select"
                                        inputLabel={"Select the school receiving this application."}
                                        identifier={FORM_FIELDS.SCHOOL_RECEIVING}
                                        options={createList(staticData, "schools", { Active: "1", Receiving_School: "1" }, "Code", "School_Name")}
                                    />
                                    <i>
                                        To locate the school within your area, click{" "}
                                        <a href="https://transportation.gssd.ca/Eligibility" target="_blank">
                                            here
                                        </a>{" "}
                                        to access our school locator.
                                    </i>
                                </>
                            )}
                    </Section>
                    {EligibilityComponent && <EligibilityComponent onContinue={beforeSubmit} />}
                </>
            )}
        </Container>
    );
}

export default InitialForm;